import React, { Suspense, lazy, useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ConfigProvider, Modal, Button, Row, Col } from 'antd';
import { Slide, ToastContainer } from 'react-toastify';

import toast from './utils/toast';
import { commonActions } from '@/actions/commonAction';
import { API_URL } from './services/axios';
import { io } from 'socket.io-client';
import { commonConstants } from './actions/constants/commonConstants';
import { Spinner } from './components/Spinner';
import { appRoutes } from './utils/routeUtils/routeConfig';
import { renderRoutes } from './utils/routeUtils/routeRenderer';
import { role } from './utils/constants';
import { compareRoutes, FlattenedRoute } from './utils/routeUtils/routeCompare';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { routeActions } from './actions/routesAction';
import RouteTree from './components/route/routeTree';
import ServerStatus from './components/serverStatus';
import "./index.css"


interface EditableRoute extends FlattenedRoute {
  _id?: string;
  status?: 'missing' | 'outdated';
  list?: any[];
}

const RETRY_INTERVAL = 5 * 60;


const App: React.FC = () => {
  const dispatch = useDispatch();
  const notification = useSelector((state: any) => state.notificationReducer || {})
  const auth = useSelector((state: any) => state.authReducer?.authData);
  const isLoading = useSelector((state: any) => state.setLoading.isLoading);
  const routes = useSelector((state: any) => state.getRoutes);
  const roleList = useSelector((state: any) => state.getRoleReducer);
  const routeInsert = useSelector((state: any) => state.bulkRouteInsert);


  const [showSyncModal, setShowSyncModal] = useState(false);
  const [editingRoutes, setEditingRoutes] = useState<EditableRoute[]>([]);
  const [roles, setRoles] = useState<any[]>();
  const [routePaths, setRoutePaths] = useState<any[]>([]);
  const [retryCountdown, setRetryCountdown] = useState(RETRY_INTERVAL);
  const [serverDown, setServerDown] = useState(false);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const retryRef = useRef<NodeJS.Timeout | null>(null);

  const splitRoutes = (routes: any[]) => {
    const middle = Math.ceil(routes.length / 2);
    return [
      routes.slice(0, middle),
      routes.slice(middle),
    ];
  };

  const handleSyncAll = async () => {
    try {
      const missingRoutes = editingRoutes.filter(r => r.status === 'missing');
      const formatRoutesForBackend = (routeList: EditableRoute[]) => {
        return routeList.map((route: any) => {
          console.log('route', route);
          const originalRoute = routes?.list?.find((r: any) => r.path === route.path);

          return {
            ...(originalRoute?._id && { _id: originalRoute._id }), // Include _id if updating
            path: route.path,
            key: route.key,
            label: route.label,
            access: route.access || [],
            allowedRoles: route.allowedRoles?.map((role: any) => {
              if (typeof role === 'string') {
                const matchedRole = roles?.find((r: any) => (r.role || r) === role);
                return matchedRole ? { role: matchedRole.role, roleId: matchedRole._id } : { role: role };
              }
              return { role: role.role, roleId: role._id || role.roleId };
            }) || [],
            order: route.order,
            isPublic: route.isPublic || false,
            isActive: route.isActive !== undefined ? route.isActive : true,
            isDefault: route.isDefault || false,
            extra: originalRoute?.extra || [],
            children: route.children?.map((child: any) => ({
              ...(child._id && { _id: child._id }),
              key: child.key,
              path: child.path,
              label: child.label,
              access: child.access || [],
              allowedRoles: child.allowedRoles?.map((role: any) => {
                if (typeof role === 'string') {
                  const matchedRole = roles?.find((r: any) => (r.role || r) === role);
                  return matchedRole ? { role: matchedRole.role, roleId: matchedRole._id } : { role: role };
                }
                return { role: role.role, roleId: role._id || role.roleId };
              }) || [],
              extra: originalRoute?.extra || []
            })) || []
          };
        });
      };
      const formattedMissing = formatRoutesForBackend(missingRoutes);
      if (formattedMissing.length > 0) {
        dispatch(routeActions.bulkInsertRoutesRequest(formattedMissing));
      }
    } catch (error) {
      toast.error('Failed to sync routes');
    }
  };

  const handleDismiss = () => {
    setShowSyncModal(false);
  };
  const clearTimers = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (retryRef.current) clearTimeout(retryRef.current);
  };

  const startRetryTimer = useCallback(() => {
    clearTimers();
    setRetryCountdown(RETRY_INTERVAL);

    countdownRef.current = setInterval(() => {
      setRetryCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    retryRef.current = setTimeout(() => {
      dispatch(commonActions.getRoutes());
    }, RETRY_INTERVAL * 1000);
  }, [dispatch]);

  const handleRetryNow = useCallback(() => {
    clearTimers();
    dispatch(commonActions.getRoutes());
  }, [dispatch]);

  useEffect(() => {
    if (roleList.isSuccess) {
      const filteredRoles = roleList.roles;
      setRoles(filteredRoles);
    }
  }, [roleList]);

  useEffect(() => {
    if (auth?.tenantId) dispatch(commonActions.getListRole(auth.tenantId[0]));
  }, [auth, dispatch]);
  useEffect(() => {
    if (notification.isSuccess) {
      if (notification.message) {
        toast.success(notification.message);
      }
    }
    if (notification.isError) {
      if (notification.message) {
        toast.error(notification.message);
      }
    }
  }, [notification]);

  useEffect(() => {
    if (auth?.token) {
      dispatch({ type: commonConstants.fetchNotificationSettingsRequest })
      dispatch({ type: commonConstants.getNotificationCountRequest });
      dispatch(commonActions.getRoutes());
      if (auth.role !== role.SUPER_ADMIN_ROLE)
        dispatch(commonActions.getAccessibleRoutes());
    }
  }, [auth?.token]);
  useEffect(() => {
    if (!auth?.token) return;

    const socket = io(API_URL, {
      auth: { token: auth.token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    socket.on('connect', () => {
      console.log('Socket.IO connected:', socket.id);

      const studioId = (auth as any)?.studioId?.[0];
      if (studioId) {
        socket.emit('join:studio', studioId);
      }
    });
    socket.on('notification', (payload: any) => {
      if (payload?.type === 'notification_count') {
        dispatch({
          type: commonConstants.getNotificationCountSuccess,
          payload: payload.count,
        });
      }
    });
    socket.on('album_selection_submitted', (payload: any) => {
      dispatch({
        type: 'EVENT_ALBUM_SELECTION_FETCH_WORKSPACE_REQUEST', payload: {
          eventId: payload?.eventId,
          studioId: (auth as any)?.studioId?.[0],
        }
      });
    });
    socket.on('disconnect', (reason: string) => {
      console.log('Socket.IO disconnected:', reason);
    });
    socket.on('connect_error', (err: Error) => {
      console.warn('Socket.IO connection error:', err.message);
    });
    return () => {
      socket.disconnect();
    };
  }, [auth?.token]);

  useEffect(() => {
    if (auth?.role === role.SUPER_ADMIN_ROLE && routes?.list && routes?.list.length > 0) {
      const comparison = compareRoutes(appRoutes, routes?.list);
      const allRoutes = [
        ...comparison.missing.map(r => ({ ...r, status: 'missing' as const })),
      ];
      setEditingRoutes(allRoutes);
      if (comparison.missing.length > 0) {
        setShowSyncModal(true);
      }
    }
  }, [auth?.role, routes?.list]);

  useEffect(() => {
    dispatch(commonActions.getRoutes());
  }, [dispatch]);

  useEffect(() => {
    if (routeInsert.isSuccess) {
      if (window.location.pathname === '/routes') setShowSyncModal(false);
      else window.location.href = '/routes';
      routeInsert.isSuccess = false;
    }
  }, [routeInsert.isSuccess]);
  useEffect(() => {
    if (routes.isFailure) {
      setServerDown(true);
      startRetryTimer();
    }
    if (routes.isSuccess && routes?.list) {
      setServerDown(false);
      clearTimers();
      setRoutePaths(routes.list);
    }
  }, [routes.isSuccess, routes.isFailure]);

  useEffect(() => {
    return () => clearTimers(); // cleanup on unmount
  }, []);
  if (serverDown) {
    console.log('Server is down. Displaying ServerStatus component.');
    return <ServerStatus retryIn={retryCountdown} onRetryNow={handleRetryNow} />;
  }

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
        limit={4}
      />
      {isLoading && <Spinner />}
      <Modal
        width={700}
        title={
          <span>
            <ExclamationCircleOutlined
              style={{ color: "#faad14", marginRight: 8 }}
            />
            Route Synchronization Required
          </span>
        }
        open={showSyncModal}
        onCancel={handleDismiss}
        footer={[
          <Button key="dismiss" onClick={handleDismiss}>
            Later
          </Button>,
          <Button key="sync" type="primary" style={{ backgroundColor: "#4f46e5" }} onClick={handleSyncAll}>
            Sync Now
          </Button>,
        ]}
      >
        <p>
          Your route configuration is out of sync with the database:
        </p>

        <Row gutter={[16, 16]}>
          {splitRoutes(editingRoutes).map((column, index) => (
            <Col xs={24} md={12} key={index}>
              <RouteTree routes={column} />
            </Col>
          ))}
        </Row>

        <p style={{ marginTop: 16 }}>
          Would you like to synchronize the routes now?
        </p>
      </Modal>
      <ConfigProvider theme={{ token: { colorPrimary: '#4f46e5', borderRadius: 8, }, }} >
        {routePaths && routePaths.length > 0 && <Router>
          <Suspense fallback={<Spinner />}>
            <Routes>
              {renderRoutes(routePaths)}
            </Routes>
          </Suspense>
        </Router>
          // :
          //   <Spinner />
        }
      </ConfigProvider>
    </>
  );
};

export default App;

